import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Box, IconButton } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';

const autoParagraphs = (text = '') => {
  return text.replace(/<br\s*\/?>/gi, '\n').replace(/\n{3,}/g, '\n\n');
};

const ChatMessage = ({ content, isUser, isTyping = false, message, onEdit, onDelete, compact = false }) => {
  if (isTyping) {
    return (
      <Box
        sx={{
          alignSelf: 'flex-start',
          backgroundColor: '#f7f7f8',
          color: '#666',
          padding: compact ? '8px 12px' : '14px 18px',
          borderRadius: compact ? '10px' : '14px',
          maxWidth: '75%',
          fontSize: compact ? '1rem' : '14px',
          fontStyle: 'italic'
        }}
      >
        Phoenix AI is typing…
      </Box>
    );
  }

  // Compact mode: skip the centering wrapper entirely
  if (compact) {
    return isUser ? (
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          position: 'relative',
          '&:hover .msg-actions': { opacity: 1 }
        }}
      >
        <Box
          sx={{
            bgcolor: '#8b2c2c',
            color: '#fff',
            px: 1.75,
            py: 1,
            borderRadius: '10px',
            width: 'fit-content',
            maxWidth: '85%',
            fontSize: '1rem',
            lineHeight: 1.6,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            overflowWrap: 'break-word'
          }}
        >
          {content}
        </Box>

        {message?.id && (
          <Box
            className="msg-actions"
            sx={{
              opacity: 0,
              transition: 'opacity 0.2s ease',
              display: 'flex',
              gap: 0.5,
              mt: 0.25
            }}
          >
            <IconButton size="small" onClick={() => onEdit?.(message)} sx={{ p: 0.25 }}>
              <EditOutlinedIcon sx={{ fontSize: 14 }} />
            </IconButton>
            <IconButton size="small" onClick={() => onDelete?.(message.id)} sx={{ p: 0.25 }}>
              <DeleteOutlineOutlinedIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        )}
      </Box>
    ) : (
      <Box
        sx={{
          bgcolor: '#f7f7f8',
          color: '#111',
          px: 1.75,
          py: 1,
          borderRadius: '8px',
          width: '100%',
          fontSize: '1rem',
          lineHeight: 1.6
        }}
      >
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            h1: ({ children }) => <h1 style={{ margin: '8px 0 4px', fontSize: '1.25rem' }}>{children}</h1>,
            h2: ({ children }) => <h2 style={{ margin: '6px 0 3px', fontSize: '1.125rem' }}>{children}</h2>,
            h3: ({ children }) => <h3 style={{ margin: '5px 0 2px', fontSize: '1.06rem' }}>{children}</h3>,
            p: ({ children }) => <p style={{ margin: '0 0 6px', lineHeight: 1.6 }}>{children}</p>,
            ul: ({ children }) => <ul style={{ paddingLeft: 16, margin: '0 0 6px' }}>{children}</ul>,
            ol: ({ children }) => <ol style={{ paddingLeft: 16, margin: '0 0 6px' }}>{children}</ol>,
            li: ({ children }) => <li style={{ marginBottom: 2, lineHeight: 1.6 }}>{children}</li>,
            table: ({ children }) => (
              <div style={{ overflowX: 'auto', margin: '8px 0' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>{children}</table>
              </div>
            ),
            thead: ({ children }) => <thead style={{ backgroundColor: '#f1f1f1' }}>{children}</thead>,
            tbody: ({ children }) => <tbody>{children}</tbody>,
            tr: ({ children }) => <tr style={{ borderBottom: '1px solid #e0e0e0' }}>{children}</tr>,
            th: ({ children }) => <th style={{ textAlign: 'left', padding: '8px', fontWeight: 600, borderBottom: '1px solid #ddd' }}>{children}</th>,
            td: ({ children }) => <td style={{ padding: '8px', verticalAlign: 'top', lineHeight: 1.6 }}>{children}</td>
          }}
        >
          {autoParagraphs(content)}
        </ReactMarkdown>
      </Box>
    );
  }

  // Full mode (non-compact): original layout with centering
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        mb: 3
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 720,
          px: 2
        }}
      >
        {isUser ? (
          <Box
            sx={{
              ml: 'auto',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
              position: 'relative',
              '&:hover .msg-actions': { opacity: 1 }
            }}
          >
            <Box
              sx={{
                ml: 'auto',
                bgcolor: '#8b2c2c',
                color: '#fff',
                px: 2,
                py: 1.5,
                borderRadius: '14px',
                display: 'inline-block',
                minWidth: { xs: 180, sm: 220 },
                maxWidth: { xs: '85%', sm: '70%' },
                fontSize: '15px',
                lineHeight: 1.6,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                overflowWrap: 'break-word'
              }}
            >
              {content}
            </Box>

            {message?.id && (
              <Box
                className="msg-actions"
                sx={{
                  opacity: 0,
                  transition: 'opacity 0.2s ease',
                  display: 'flex',
                  gap: 0.5,
                  mt: 0.5
                }}
              >
                <IconButton size="small" onClick={() => onEdit?.(message)}>
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>

                <IconButton size="small" onClick={() => onDelete?.(message.id)}>
                  <DeleteOutlineOutlinedIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Box>
        ) : (
          <Box
            sx={{
              bgcolor: '#f7f7f8',
              color: '#111',
              px: 2,
              py: 1.5,
              borderRadius: '12px',
              fontSize: '15px',
              lineHeight: 1.75
            }}
          >
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                h1: ({ children }) => <h1 style={{ margin: '20px 0 10px', fontSize: '1.25rem' }}>{children}</h1>,
                h2: ({ children }) => <h2 style={{ margin: '18px 0 8px', fontSize: '1.1rem' }}>{children}</h2>,
                h3: ({ children }) => <h3 style={{ margin: '14px 0 6px', fontSize: '1rem' }}>{children}</h3>,
                p: ({ children }) => <p style={{ margin: '0 0 12px', lineHeight: 1.75 }}>{children}</p>,
                ul: ({ children }) => <ul style={{ paddingLeft: 20, margin: '0 0 12px' }}>{children}</ul>,
                ol: ({ children }) => <ol style={{ paddingLeft: 20, margin: '0 0 12px' }}>{children}</ol>,
                li: ({ children }) => <li style={{ marginBottom: 6, lineHeight: 1.7 }}>{children}</li>,
                table: ({ children }) => (
                  <div style={{ overflowX: 'auto', margin: '16px 0' }}>
                    <table
                      style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        fontSize: '14px'
                      }}
                    >
                      {children}
                    </table>
                  </div>
                ),
                thead: ({ children }) => <thead style={{ backgroundColor: '#f1f1f1' }}>{children}</thead>,

                tbody: ({ children }) => <tbody>{children}</tbody>,

                tr: ({ children }) => <tr style={{ borderBottom: '1px solid #e0e0e0' }}>{children}</tr>,

                th: ({ children }) => (
                  <th
                    style={{
                      textAlign: 'left',
                      padding: '10px',
                      fontWeight: 600,
                      borderBottom: '1px solid #ddd'
                    }}
                  >
                    {children}
                  </th>
                ),

                td: ({ children }) => (
                  <td
                    style={{
                      padding: '10px',
                      verticalAlign: 'top',
                      lineHeight: 1.6
                    }}
                  >
                    {children}
                  </td>
                )
              }}
            >
              {autoParagraphs(content)}
            </ReactMarkdown>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ChatMessage;
